// client/src/components/configuracoes/horarios/HorarioAtendimentoPage.js - VERSÃO COM FILTRO REFINADO PELA AGENDA

import React, { useState, useEffect, useCallback, useMemo, Fragment } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useHorarios, useMutateHorarios } from '../../../hooks/useHorarios';
import { useAccountUsers, useMyAccountDetails } from '../../../hooks/useAccounts';
import { useRecursos } from '../../../hooks/useRecursos';
import { useUnidades } from '../../../hooks/useUnidades';
import { useAgendaByEntidadeQuery } from '../../../hooks/useAgendas'; // ✅ HOOK IMPORTADO
import { PlusCircle, Loader2, RefreshCcw, ArrowLeft, Clock, Filter as FilterIcon, SquarePen, Trash2 } from 'lucide-react';
import HorarioForm from './HorarioForm';
import HorarioRow from './HorarioRow';
import FiltroHorarios from './FiltroHorarios';
import ConfiguracoesAgenda from './ConfiguracoesAgenda';
import { FormStepper } from '../../shared/FormStepper';
import ActionsDrawer from '../../common/ActionsDrawer';
import PageHeader from '../../layout/PageHeader';

const TAB_STEPS = ['horarios', 'configuracoes'];
const TAB_LABELS = ['Horário de Atendimento', 'Configurações da Agenda'];

const HorarioAtendimentoPage = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const [entidadeSelecionada, setEntidadeSelecionada] = useState({ id: null, tipo: null, nome: '', obj: null });
    const [showFilters, setShowFilters] = useState(true);
    const [activeTab, setActiveTab] = useState('horarios');
    
    // Hooks de Dados
    const { data: accountDetails } = useMyAccountDetails();
    const accountId = accountDetails?._id || accountDetails?.id;

    const { data: accountUsersData, isLoading: usersLoading } = useAccountUsers();
    const { data: recursosDataResponse, isLoading: recursoLoading } = useRecursos(accountId, { limit: 1000 });
    const { data: unidadesDataResponse, isLoading: unidadesLoading } = useUnidades(); 
    
    // ✅ NOVO: Busca as configurações de Agenda e Permissões da Entidade Atual
    const { data: agendaData } = useAgendaByEntidadeQuery(
        entidadeSelecionada?.tipo, 
        entidadeSelecionada?.id, 
        { enabled: !!entidadeSelecionada?.tipo && !!entidadeSelecionada?.id }
    );

    const paramsBuscaHorarios = useMemo(() => {
        const params = new URLSearchParams(location.search);
        return {
            tipoEntidade: params.get('tipo'),
            entidadeAgendavel: params.get('entidadeId'),
        };
    }, [location.search]);

    const { data: horarios, isLoading: horarioLoading, refetch: refetchHorarios, isFetching: isHorariosFetching } = useHorarios(
        paramsBuscaHorarios,
        { enabled: !!paramsBuscaHorarios.entidadeAgendavel && !!paramsBuscaHorarios.tipoEntidade }
    );

    const profissionaisMapped = useMemo(() => {
        const arrayUsers = Array.isArray(accountUsersData) ? accountUsersData : accountUsersData?.data || [];
        return arrayUsers
            .filter(u => u != null)
            .map(u => {
                const actualUser = u.user || u;
                return {
                    _id: String(actualUser._id || actualUser.id),
                    nome: actualUser.name || actualUser.nome,
                    vinculoUnidades: u.unidades || actualUser.unidades || [] 
                };
            });
    }, [accountUsersData]);
    
    const recursosComAgenda = useMemo(() => {
        const arrayRecursos = Array.isArray(recursosDataResponse) ? recursosDataResponse : recursosDataResponse?.data || [];
        return arrayRecursos.filter(r => r && r.temAgendaPropria === true).map(r => ({ ...r, _id: String(r._id || r.id), nome: r.nome }));
    }, [recursosDataResponse]);

    const unidades = useMemo(() => {
        const arrayUnidades = Array.isArray(unidadesDataResponse) ? unidadesDataResponse : unidadesDataResponse?.data || [];
        return arrayUnidades.map(u => ({ ...u, _id: String(u._id || u.id), nome: u.nome }));
    }, [unidadesDataResponse]);
    
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const tipo = params.get('tipo');
        const entidadeId = params.get('entidadeId');

        let entidadeEncontrada = { id: null, tipo: null, nome: '', obj: null };

        if (tipo && entidadeId && !usersLoading && !recursoLoading && !unidadesLoading) {
            if (tipo === 'User') {
                const obj = profissionaisMapped.find(item => String(item._id) === String(entidadeId));
                if (obj) entidadeEncontrada = { id: entidadeId, tipo: 'User', nome: obj.nome, obj };
            } else if (tipo === 'Recurso') {
                const obj = recursosComAgenda.find(item => String(item._id) === String(entidadeId));
                if (obj) entidadeEncontrada = { id: entidadeId, tipo: 'Recurso', nome: obj.nome, obj };
            } else if (tipo === 'Unidade') {
                const obj = unidades.find(item => String(item._id) === String(entidadeId));
                if (obj) entidadeEncontrada = { id: entidadeId, tipo: 'Unidade', nome: obj.nome, obj };
            }
        }
        
        setEntidadeSelecionada(entidadeEncontrada);
    }, [location.search, profissionaisMapped, recursosComAgenda, unidades, usersLoading, recursoLoading, unidadesLoading]);

    const updateURL = useCallback((paramsToUpdate) => {
        const params = new URLSearchParams();
        Object.entries(paramsToUpdate).forEach(([key, value]) => {
            if (value) params.set(key, value);
        });
        navigate({ search: params.toString() });
    }, [navigate]);

    const handleEntidadeSelect = useCallback((entidade) => {
        updateURL({ tipo: entidade.tipo, entidadeId: entidade.id });
    }, [updateURL]);
    
    const showGerenciamento = !!entidadeSelecionada.id;

    // ✅ LÓGICA ABSOLUTA: Extração controlada de unidades permitidas baseada na Agenda da Entidade
    const unidadesDisponiveis = useMemo(() => {
        if (!entidadeSelecionada.obj) return [];
        const { tipo, obj } = entidadeSelecionada;

        // 1. PRIORIDADE MÁXIMA: Tenta usar a lista rigorosa "unidadesVinculadas" configurada na aba de Configurações de Agenda
        if (agendaData?.permissoes?.unidadesVinculadas && agendaData.permissoes.unidadesVinculadas.length > 0) {
            const idsVinculados = agendaData.permissoes.unidadesVinculadas.map(v => 
                typeof v === 'object' ? String(v._id || v.id || v.$oid) : String(v)
            );
            
            const filtradas = unidades.filter(u => idsVinculados.includes(String(u._id)));
            if (filtradas.length > 0) {
                return filtradas; // Retorna exatamente o que a Agenda permitiu!
            }
        }

        // 2. FALLBACKS DE SEGURANÇA: Se a agenda não limitou nada, procura no objeto da entidade
        if (tipo === 'User') {
            if (obj.vinculoUnidades && obj.vinculoUnidades.length > 0) {
                return obj.vinculoUnidades.map(vu => {
                    if (typeof vu === 'object' && vu.nome) return vu; 
                    const foundId = typeof vu === 'object' ? (vu._id || vu.id) : vu; 
                    const match = unidades.find(u => u._id === String(foundId));
                    return match || vu;
                });
            }
            // Retorna vazio caso o usuário não tenha nenhuma unidade associada
            return [];
        }

        if (tipo === 'Recurso') {
            if (obj.unidadesPermitidas && obj.unidadesPermitidas.length > 0) {
                const idsPermitidos = obj.unidadesPermitidas.map(u => typeof u === 'object' ? String(u._id || u.id) : String(u));
                return unidades.filter(u => idsPermitidos.includes(String(u._id)));
            }
            return obj.unidade ? [obj.unidade] : unidades;
        }

        if (tipo === 'Unidade') {
            return [obj]; 
        }

        return [];
    }, [entidadeSelecionada, unidades, agendaData]);

    return (
        <Fragment>
            <div className="page-management-container">
                {/* Antes montava o cabecalho na mao (page-header/page-title
                    direto), sem o dropdown ⋮ que o PageHeader compartilhado
                    ja da de graca no mobile */}
                <PageHeader
                    icon={<Clock />}
                    title="Horários e Configurações"
                    actions={
                        <button type="button" onClick={() => setShowFilters(p => !p)} className="action-button action-button-secondary">
                            <FilterIcon size={18} className="icon"/> {showFilters ? 'Ocultar Filtros' : 'Exibir Filtros'}
                        </button>
                    }
                />
                {showFilters &&
                    <FiltroHorarios
                        onSelectEntidade={handleEntidadeSelect}
                        entidadeAtual={entidadeSelecionada}
                    />
                }
            </div>
            
            {showGerenciamento && (
                <div className="content-panel mt-6">
                    <div className="table-header">
                        <h2>Gerenciando: {entidadeSelecionada.nome}</h2>
                    </div>
                    <FormStepper
                        currentStep={TAB_STEPS.indexOf(activeTab) + 1}
                        steps={TAB_LABELS}
                        onStepClick={(stepNumber) => setActiveTab(TAB_STEPS[stepNumber - 1])}
                    />

                    <div className="tab-content">
                        {activeTab === 'horarios' && (
                            <HorariosSemana
                                entidadeSelecionada={entidadeSelecionada}
                                horarios={horarios || []}
                                loading={horarioLoading || isHorariosFetching}
                                refetchHorarios={refetchHorarios}
                                unidadesDisponiveis={unidadesDisponiveis}
                            />
                        )}
                        {activeTab === 'configuracoes' && (
                            <ConfiguracoesAgenda entidadeSelecionada={entidadeSelecionada} />
                        )}
                    </div>
                </div>
            )}
        </Fragment>
    );
};

const HorariosSemana = ({ entidadeSelecionada, horarios, loading, refetchHorarios, unidadesDisponiveis }) => {
    const [showForm, setShowForm] = useState(false);
    const [current, setCurrent] = useState(null);
    const [selectedHorario, setSelectedHorario] = useState(null);
    const { deleteHorario } = useMutateHorarios();
    const isMutating = deleteHorario.isPending;

    const handleToggleForm = () => { if (showForm) setCurrent(null); setShowForm(p => !p); };
    const handleEdit = (horario) => { setCurrent(horario); setShowForm(true); setSelectedHorario(null); window.scrollTo({ top: 0, behavior: 'smooth' }); };
    const handleDelete = (horarioId) => { deleteHorario.mutate(horarioId, { onSuccess: () => setSelectedHorario(null) }); };

    const horariosSemanais = useMemo(() => Array.isArray(horarios) ? horarios.filter(h => h.tipo === 'SEMANAL') : [], [horarios]);
    const datasEspecificas = useMemo(() => Array.isArray(horarios) ? horarios.filter(h => h.tipo === 'DATA_UNICA') : [], [horarios]);

    return (
        <div>
            <div className="table-header mt-6">
                <h2>Agenda de Trabalho</h2>
                <div className="actions-bar">
                    <button type="button" onClick={() => refetchHorarios()} className="action-icon-button" title="Atualizar"><RefreshCcw size={16}/></button>
                    <button type="button" onClick={handleToggleForm} className="action-button action-button-primary">
                        {showForm ? <ArrowLeft size={18} className="icon" /> : <PlusCircle size={18} className="icon"/>}
                        {showForm ? 'Voltar' : 'Adicionar Regra'}
                    </button>
                </div>
            </div>
            {showForm ? (
                <div className="mt-4">
                    <HorarioForm 
                        entidadeId={entidadeSelecionada.id}
                        tipoEntidade={entidadeSelecionada.tipo}
                        onCloseForm={() => setShowForm(false)} 
                        current={current}
                        clearCurrent={() => setCurrent(null)}
                        horarios={horarios}
                        unidadesDisponiveis={unidadesDisponiveis}
                    />
                </div>
            ) : (
                <div className="table-wrapper mt-4">
                    {loading ? (<div className="table-loading-state"><Loader2 size={24} className="animate-spin" /></div>) : (
                        <table className="styled-table">
                            <thead><tr><th>Dia / Data</th><th>Tipo</th><th>Unidade</th><th>Início</th><th>Fim</th><th>Intervalos</th><th className="text-right">Ações</th></tr></thead>
                            <tbody>
                                {horariosSemanais.length > 0 && horariosSemanais.map(h => <HorarioRow key={h._id} horario={h} onOpenActions={setSelectedHorario} isMutating={isMutating} />)}

                                {datasEspecificas.length > 0 && datasEspecificas.map(h => <HorarioRow key={h._id} horario={h} onOpenActions={setSelectedHorario} isMutating={isMutating} />)}

                                {(!horarios || horarios.length === 0) && (
                                    <tr><td colSpan="7" className="td-empty">Nenhum horário cadastrado para esta seleção.</td></tr>
                                )}
                            </tbody>
                        </table>
                    )}
                </div>
            )}

            <ActionsDrawer
                open={!!selectedHorario}
                onClose={() => setSelectedHorario(null)}
                title={selectedHorario?.tipo === 'SEMANAL' ? 'Horário Semanal' : 'Data Única'}
                actions={selectedHorario ? [
                    {
                        key: 'editar',
                        label: 'Editar',
                        icon: <SquarePen size={20} className="icon" />,
                        onClick: () => handleEdit(selectedHorario),
                    },
                    {
                        key: 'excluir',
                        label: 'Excluir',
                        icon: <Trash2 size={20} className="icon" />,
                        onClick: () => handleDelete(selectedHorario._id),
                        destructive: true,
                        loading: isMutating,
                    },
                ] : []}
            />
        </div>
    );
};

export default HorarioAtendimentoPage;